import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Calendar } from "@untitledui/icons";
import { type ReactNode, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { TabHeader } from "@/components/base/tab-header";
import { EmptyValue } from "@/components/common/EmptyValue";
import type { HistoryEntry } from "@/components/Organisms/HistoryList";
import { HistoryList } from "@/components/Organisms/HistoryList";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useAuditUserNames } from "@/hooks/use-audit-user-names";
import type { AuditLogDisplay } from "@/services/job/job.types";
import { userQueryOptions } from "@/services/user";
import { formatLongDate } from "@/utils/utils";
import { SearchSchema } from "../$userId";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/activity",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/activity",
	});
	const userId = params.userId;

	const userById = useQuery(userQueryOptions.detail(userId));
	const { createdByName, updatedByName } = useAuditUserNames(
		userById.data?.createdBy,
		userById.data?.updatedBy,
	);

	const pageSize = 10;
	const historyCountQuery = useQuery(userQueryOptions.history.count(userId));

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery(
			userQueryOptions.history.infinite(
				userId,
				pageSize,
				historyCountQuery.data,
			),
		);

	const historyEntries: HistoryEntry[] = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flat().map(
			(log: AuditLogDisplay): HistoryEntry => ({
				id: log.id,
				action: log.action,
				date: log.date,
				fieldName: log.fieldName,
				oldValue: log.oldValue,
				newValue: log.newValue,
				user: log.user
					? {
							id: log.user.id,
							displayName: log.user.displayName,
							email: log.user.email,
							firstName: log.user.firstName,
							middleName: log.user.middleName,
							lastName: log.user.lastName,
						}
					: null,
				entityType: log.entityType,
			}),
		);
	}, [data]);

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: "0px 0px 300px 0px",
	});

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	return (
		<div className="space-y-6">
			<TabHeader title={t("admin.users.tabs.platform.activity.title")} />

			<div className="bg-(--color-bg-secondary) rounded-lg p-4">
				<div className="space-y-4">
					<DetailFieldGrid>
						<DetailFieldSection>
							<ActivityField
								label={t("admin.users.tabs.organization.details.created")}
								value={userById.data?.createdAt}
							/>
							<DetailField
								label={t(
									"admin.users.tabs.organization.details.createdBy",
								).toUpperCase()}
								valueClassName="font-bold text-gray-900"
								value={
									createdByName ? (
										<span className="font-bold text-gray-900">
											{createdByName}
										</span>
									) : (
										<EmptyValue />
									)
								}
							/>
							<ActivityField
								label={t("admin.users.tabs.organization.details.lastLogin")}
								value={userById.data?.lastLoggedInAt}
								includeTime
							/>
						</DetailFieldSection>
						<DetailFieldSection>
							<ActivityField
								label={t("admin.users.tabs.organization.details.lastUpdate")}
								value={userById.data?.updatedAt}
							/>
							<DetailField
								label={t(
									"admin.users.tabs.organization.details.lastUpdateBy",
								).toUpperCase()}
								valueClassName="font-bold text-gray-900"
								value={
									updatedByName ? (
										<span className="font-bold text-gray-900">
											{updatedByName}
										</span>
									) : (
										<EmptyValue />
									)
								}
							/>
						</DetailFieldSection>
					</DetailFieldGrid>
				</div>
			</div>

			<div className="space-y-3 rounded-lg border border-gray-200 bg-white p-6">
				<div>
					<h4 className="text-lg font-semibold text-gray-900">
						{t("admin.users.tabs.activity.history.title")}
					</h4>
					<p className="text-sm text-gray-500">
						{t("admin.users.tabs.activity.history.description")}
					</p>
				</div>
				<HistoryList
					entries={historyEntries}
					isLoading={isLoading}
					emptyMessage={t("admin.users.tabs.activity.history.empty")}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					loadMoreRef={loadMoreRef}
					located="jobs"
				/>
			</div>
		</div>
	);
}

function ActivityField({
	label,
	value,
	includeTime,
}: {
	label: string;
	value: string | null | undefined;
	includeTime?: boolean;
}): ReactNode {
	return (
		<DetailField
			label={label}
			valueClassName="font-bold text-gray-900"
			value={
				value ? (
					<div className="flex items-center gap-2">
						<Calendar size={15} className="text-gray-400" />
						{formatLongDate(
							value,
							includeTime ? { includeTime: true } : undefined,
						)}
					</div>
				) : (
					<EmptyValue />
				)
			}
		/>
	);
}
